// ──────────────────────────────────────────────────────────────────────
// Event Description Generator — Phase 2C (template-based, no AI)
// Pure function — deterministic given answers + variantSeed. Returns a
// short, clean description composed from 4 lines:
//   1. Opening (event_type)
//   2. Access (access_style)
//   3. Privacy (privacy_level)
//   4. Guest framing (guest_type)
//   5. Closing tone (event_type)
// Lines with no content for a given answer are skipped so the result
// stays tight. `variantSeed` cycles through 3 phrasing variants per line
// so "Regenerate" produces subtly different output without external AI.
// ──────────────────────────────────────────────────────────────────────

const OPENINGS = {
  'Private Experience': [
    'An exclusive private experience designed for curated connections and elevated energy.',
    'A refined private gathering built around curated company and quiet confidence.',
    'A private, members-minded experience with carefully managed access and atmosphere.',
  ],
  'Curated Gathering': [
    'A thoughtfully curated gathering built around connection, energy, and shared experience.',
    'A carefully curated evening designed for meaningful moments and good company.',
    'A curated social gathering shaped around tone, taste, and the right crowd.',
  ],
  'House Party': [
    'A relaxed house party with good energy, familiar faces, and an easygoing vibe.',
    'A low-key house party with great people, great music, and an open-door feel.',
    'A chill house party built around connection, conversation, and staying a while.',
  ],
  'Club / Venue': [
    'A high-energy event with music, movement, and a packed atmosphere.',
    'A full-venue night built around sound, crowd energy, and peak momentum.',
    'A club-night experience with a full lineup, a full room, and full commitment.',
  ],
  'Sporting Event': [
    'A live sporting experience with a focus on the game, the crowd, and the moment.',
    'A live-action event built around the match, the fans, and the atmosphere in the stands.',
    'A live game-day experience — good seats, loud crowd, and real moments worth showing up for.',
  ],
  Custom: [
    'A standout event shaped around intention, atmosphere, and the people in the room.',
    'A distinctive event designed around its own tone and its own kind of night.',
    'An event put together with care — its own pace, its own crowd, its own story.',
  ],
};

const ACCESS_LINES = {
  'Approval Required': [
    'Access is limited and subject to approval.',
    'Entry is moderated — all requests are reviewed before confirmation.',
    'Guests are approved individually to protect the atmosphere.',
  ],
  'Application + Review': [
    'Entry is managed through a short review process.',
    'Guests go through a quick application and review before being confirmed.',
    'Admission is handled via a brief review to keep the room balanced.',
  ],
  'Instant Access': [
    'Entry is open while capacity allows.',
    'Open access until we reach capacity.',
    'Secure your spot — entry is first-come while capacity holds.',
  ],
};

const PRIVACY_LINES = {
  'Hidden Location': [
    'Location details will be shared after confirmation.',
    'Address is kept private and released to confirmed guests.',
    'The venue will be shared privately once your spot is confirmed.',
  ],
  'Fully Private': [
    'This is a private event with restricted visibility.',
    'A fully private event — details are shared only within the guest circle.',
    'A closed, invite-shaped event with no public visibility.',
  ],
  'Public Listing': [],
};

const GUEST_LINES = {
  'Couples Only': [
    'Designed for paired guests.',
    'Open to couples — this event is shaped for paired attendance.',
    'A couples-focused environment throughout the night.',
  ],
  'Singles + Couples': [
    'Open to both singles and couples.',
    'A welcoming mix of singles and couples.',
    'Balanced for singles and couples alike.',
  ],
  'Invite Only': [
    'Entry is limited to invited guests.',
    'This is an invite-only guest list.',
    'Attendance is by invitation only.',
  ],
  'General Admission': [
    'Open to all approved guests.',
    'General admission — open to all who secure a pass.',
    'Open entry for all ticketed attendees.',
  ],
  Custom: [],
};

const CLOSINGS = {
  'Private Experience': [
    'Come prepared for a refined and memorable night.',
    'Arrive ready for something intimate and well-kept.',
    'Plan for a private evening that rewards presence.',
  ],
  'Curated Gathering': [
    'Expect a welcoming atmosphere with a touch of sophistication.',
    'Come as you are — expect warmth, music, and the right kind of energy.',
    'Plan for a comfortable, elevated evening in good company.',
  ],
  'House Party': [
    'Simple, social, and fun.',
    'Come through, connect, and stay a while.',
    'Low pressure, high vibe — bring yourself and be present.',
  ],
  'Club / Venue': [
    'Secure your spot early and be ready for a full experience.',
    'Lock in your entry and pace yourself for a long night.',
    'Arrive early, pace yourself, and ride the full set.',
  ],
  'Sporting Event': [
    'Arrive early, get settled, and enjoy the action with fellow fans.',
    'Plan for arrival buffer and settle in for the full match.',
    'Get there on time, get loud, and soak in the game.',
  ],
  Custom: [
    'Come ready to be part of the night.',
    'Bring yourself — the rest is taken care of.',
    'Show up, be present, enjoy the moment.',
  ],
};

function pickLine(arr, seed) {
  if (!arr || arr.length === 0) return null;
  const idx = ((seed % arr.length) + arr.length) % arr.length;
  return arr[idx];
}

// Normalize event_type so both "House Party" and unknown strings work
function resolveEventType(et) {
  if (!et) return 'Custom';
  const v = et.trim();
  if (OPENINGS[v]) return v;
  return 'Custom';
}

export function generateEventDescription({
  event_type,
  guest_type,
  access_style,
  privacy_level,
} = {}, variantSeed = 0) {
  const etype = resolveEventType(event_type);

  const parts = [];
  parts.push(pickLine(OPENINGS[etype], variantSeed));

  const accessArr = ACCESS_LINES[access_style];
  const accessLine = pickLine(accessArr, variantSeed + 1);
  if (accessLine) parts.push(accessLine);

  const privacyArr = PRIVACY_LINES[privacy_level];
  const privacyLine = pickLine(privacyArr, variantSeed + 2);
  if (privacyLine) parts.push(privacyLine);

  const guestArr = GUEST_LINES[guest_type];
  const guestLine = pickLine(guestArr, variantSeed + 3);
  if (guestLine) parts.push(guestLine);

  parts.push(pickLine(CLOSINGS[etype], variantSeed + 4));

  return parts.filter(Boolean).join(' ');
}
